import { useMutation, UseMutationOptions, useQuery } from 'react-query';
import { AxiosError } from 'axios';

import { client } from '@frontend/features/common/services';
import { PagedResponse } from '@bandido/shared/dtos';
import { UserNotificationTrigger, UserNotificationTriggerFormData, UserNotificationTriggerFilters } from '../types';
import { QueryKeys } from '@frontend/features/common/types';

export const useGetUserNotificationTriggers = (
  filters?: UserNotificationTriggerFilters & { page?: number; limit?: number }
) => {
  const queryKey = [QueryKeys.getUserNotificationTriggers, filters];
  
  return useQuery(
    queryKey,
    async (): Promise<PagedResponse<UserNotificationTrigger>> => {
      const params = new URLSearchParams();
      
      if (filters?.clientId) params.append('clientId', filters.clientId);
      if (filters?.agentId) params.append('agentId', filters.agentId);
      if (filters?.workflowId) params.append('workflowId', filters.workflowId);
      if (filters?.stepId) params.append('stepId', filters.stepId);
      if (filters?.status) params.append('status', filters.status);
      if (filters?.caseId) params.append('caseId', filters.caseId.toString());
      if (filters?.createdBy) params.append('createdBy', filters.createdBy);
      if (filters?.page) params.append('page', filters.page.toString());
      if (filters?.limit) params.append('limit', filters.limit.toString());
      
      const response = await client.get(`user-notification-triggers?${params.toString()}`);
      return response.data;
    }
  );
};

export const useCreateUserNotificationTrigger = (
  options?: UseMutationOptions<UserNotificationTrigger, AxiosError, UserNotificationTriggerFormData>
) => {
  return useMutation(
    async (data: UserNotificationTriggerFormData): Promise<UserNotificationTrigger> => {
      const response = await client.post('user-notification-triggers', data);
      
      return response.data;
    },
    options
  );
};

export const useUpdateUserNotificationTrigger = (
  options?: UseMutationOptions<UserNotificationTrigger, AxiosError, { id: string; data: UserNotificationTriggerFormData }>
) => {
  return useMutation(
    async ({ id, data }: { id: string; data: UserNotificationTriggerFormData }): Promise<UserNotificationTrigger> => {
      const response = await client.patch(`user-notification-triggers/${id}`, data);
      return response.data;
    },
    options
  );
};

export const useToggleUserNotificationTrigger = (
  options?: UseMutationOptions<UserNotificationTrigger, AxiosError, { id: string; enabled: boolean }>
) => {
  return useMutation(
    async ({ id, enabled }: { id: string; enabled: boolean }): Promise<UserNotificationTrigger> => {
      const response = await client.patch(`user-notification-triggers/${id}`, { enabled });
      return response.data;
    },
    options
  );
};

export const useDeleteUserNotificationTrigger = (
  options?: UseMutationOptions<void, AxiosError, string>
) => {
  return useMutation(
    async (id: string): Promise<void> => {
      await client.delete(`user-notification-triggers/${id}`);
    },
    options
  );
};
